import NutrientViewer, {
  type List,
  type AnnotationsUnion as NutrientAnnotation,
  type Instance as NutrientInstance,
  type TextMarkupAnnotation,
} from '@nutrient-sdk/viewer'
import { useEffect } from 'react'
import {
  type DocumentLabelType,
  getLabelColor,
} from '~app/configuration/labels'

/**
 * Utils
 */
interface Label {
  annotation: NutrientAnnotation
  text?: string
}

const formatText = (text: string): string | undefined => {
  const value = text.trim()
  return value !== '' ? value : undefined
}

/**
 * Hook implementation
 */

interface Props {
  instance?: NutrientInstance
  labelType?: DocumentLabelType
  onLabeled: (labels: Label[], labelType: DocumentLabelType) => void
}

export const useLabelizer = (
  { instance, onLabeled, labelType }: Props,
  deps?: any[]
) => {
  useEffect(() => {
    if (!instance) {
      // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op cleanup for useEffect
      return () => {}
    }
    const labelColor = labelType
      ? getLabelColor(labelType)
      : NutrientViewer.Color.LIGHT_YELLOW

    if (labelType) {
      instance.setCurrentAnnotationPreset(labelType as string)
      instance.setViewState(viewState =>
        viewState.set(
          'interactionMode',
          NutrientViewer.InteractionMode.SHAPE_RECTANGLE
        )
      )
    } else {
      instance.setViewState(viewState =>
        viewState.set('interactionMode', undefined)
      )
    }

    // Setup viewer event handlers
    const annotationToHighlight = async (
      createdAnnotations: List<NutrientAnnotation>
    ) => {
      const selections = createdAnnotations.filter(
        annotation =>
          annotation instanceof NutrientViewer.Annotations.RectangleAnnotation
      )
      const highlights = await Promise.all(
        selections.map(annotation => {
          const { boundingBox } = annotation
          const rects = NutrientViewer.Immutable.List([boundingBox])
          return instance.create(
            new NutrientViewer.Annotations.HighlightAnnotation({
              pageIndex: annotation.pageIndex,
              rects,
              boundingBox,
              color: labelColor,
              name: labelType,
            })
          )
        })
      )
      await Promise.all(
        selections.map(selection => instance.delete(selection.id))
      )
      if (labelType && highlights.length) {
        const labels = await Promise.all(
          highlights.map(
            async (
              annotation: TextMarkupAnnotation[] /* We are implicitly working only with TextMarkupAnnotations */
            ) => ({
              annotation: annotation[0],
              text: formatText(
                await instance.getMarkupAnnotationText(annotation[0])
              ),
            })
          )
        )
        onLabeled(labels, labelType)
        instance.save()
      }
    }

    instance.addEventListener('annotations.create', annotationToHighlight)
    return () => {
      instance.removeEventListener('annotations.create', annotationToHighlight)
    }
  }, deps)
}
